'use client';

import React, { useEffect, useState, Suspense } from 'react';
import { motion } from 'framer-motion';

function RefundPolicyContent() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    // Initialize particles only on client side
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Refund Policy</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-slate-300 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Our commitment to customer satisfaction and fair service
            </motion.p>
          </div>
        </div>
      </section>

      {/* Refund Policy Content */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <p className="text-slate-300 mb-4">Effective Date: 01 November, 2024</p>
              <p className="text-slate-300">At 1 Dollar Cloud, we are committed to providing a reliable and satisfactory hosting experience. Our refund policy is designed to give you confidence in trying our services while ensuring fairness to all customers. Please read the following policy carefully:</p>
            </div>

            {[
              {
                title: "1. Refund Eligibility",
                content: [
                  "7-Day Refund Window: We offer a 7-day refund policy for all new hosting service purchases. If, within 7 days of your initial purchase, you are not satisfied with our hosting services for any reason, you may request a full refund of the initial payment. Refunds are granted at our sole discretion and are contingent upon verification of your compliance with these Terms."
                ]
              },
              {
                title: "2. Conditions for Refunds",
                content: [
                  "Valid Request: Refund requests must be made within 7 days from the date of your initial purchase. Requests made after this period will not be eligible for a refund.",
                  "Valid Payment Method: Refunds will be issued to the original payment method used at the time of purchase. If you used a credit or debit card, the refund will be processed back to that same card.",
                  "Non-Refundable Items: Domain registrations, third-party service charges, and any non-hosting related fees are non-refundable. Additionally, any charges for additional services or upgrades will not be refunded."
                ]
              },
              {
                title: "3. How to Request a Refund",
                content: [
                  "To request a refund, please contact our customer support team via email at info@1dollarcloud.com or through our support portal. Provide your account details, the reason for the refund request, and any relevant documentation.",
                  "Our customer support team will review your request and provide further instructions. Refunds are typically processed within 5-7 business days after verification."
                ]
              },
              {
                title: "4. Service Cancellations",
                content: [
                  "Cancellation of Services: You may cancel your hosting services at any time by following the instructions in your account settings. Cancellation requests must be submitted at least 24 hours before the end of the current billing cycle to avoid being charged for the next cycle.",
                  "Data Retention: Upon cancellation, your data may be retained for a period of time as required by law or for backup purposes. You are responsible for backing up your data regularly to avoid any loss."
                ]
              },
              {
                title: "5. Exceptions and Limitations",
                content: [
                  "Service Interruptions: Refunds will not be issued for service interruptions resulting from scheduled maintenance, technical difficulties, or issues beyond our control.",
                  "User Error: Refunds will not be provided for errors made by users, such as incorrect service selection, accidental data deletion, or issues caused by third-party software or services.",
                  "Abuse of Refund Policy: We reserve the right to limit or deny refunds to users who abuse our refund policy by making multiple refund requests or taking advantage of our services with the intent of canceling shortly after purchase."
                ]
              },
              {
                title: "6. Changes to the Refund Policy",
                content: [
                  "1 Dollar Cloud reserves the right to modify or update this Refund Policy at any time. Any changes will be effective immediately upon posting on our website. We will notify you of significant changes to this policy via email or notifications on our website.",
                  "It is your responsibility to review this Refund Policy periodically for updates. Your continued use of our services after the posting of changes will constitute your acceptance of those changes."
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                {section.content.map((paragraph, i) => (
                  <p key={i} className="text-slate-300 mb-4">{paragraph}</p>
                ))}
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-slate-800/50 rounded-xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">7. Contact Information</h2>
              <p className="text-slate-300 mb-4">If you have any questions or concerns regarding this Refund Policy or need assistance with a refund request, please contact our customer support team at:</p>
              <div className="text-slate-300">
                <p className="font-semibold">1 Dollar Cloud</p>
                <p>a service of Global Ads Media, LLC.</p>
                <p>66 West Flagler Street Miami Florida 33130 USA</p>
                <p className="mt-4">Email:</p>
                <p>info@1dollarcloud.com</p>
                <p>info@globaladsmedia.us</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-slate-300 text-center mt-8"
            >
              By using our services, you acknowledge that you have read, understood, and agree to this Refund Policy.
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default function RefundPolicyPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <RefundPolicyContent />
    </Suspense>
  );
}